import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@patient-watch/ui/form";
import { cn } from "@patient-watch/ui";
import { Minus, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { MeasurementUnit, MeasurementValue } from "../measurement";
import { nudgeByStep, resolveStep, stepDecimals } from "../inputStep";
import { fromCanonicalMeasurement, isMeasurementUnit, roundMeasurement, toCanonicalMeasurement } from "../measurement";

interface Props {
    name: string;
    label: string;
    description?: string;
    required: boolean;
    allowedUnits: MeasurementUnit[];
    min?: number | null;
    max?: number | null;
    /** How far the plus and minus buttons move. Defaults to 0.1. */
    step?: number | null;
    onStoreChange?: (value: MeasurementValue) => void;
}

const DEFAULT_MEASUREMENT_STEP = 0.1;

function defaultUnit(allowedUnits: MeasurementUnit[]): MeasurementUnit {
    return allowedUnits[0] ?? "cm";
}

function formatDisplay(value: number | null): string {
    if (value === null) return "";
    return String(value);
}

function parseDisplay(raw: string): number | null {
    const trimmed = raw.trim();
    if (trimmed === "" || trimmed === "-" || trimmed === "." || trimmed === "-.") return null;
    if (/[.,]$/.test(trimmed)) return null;
    const next = Number(trimmed);
    return Number.isFinite(next) ? next : Number.NaN;
}

export default function MeasurementInput({
    name,
    label,
    description,
    required,
    allowedUnits,
    min,
    max,
    step,
    onStoreChange,
}: Props) {
    const stepSize = resolveStep(step, DEFAULT_MEASUREMENT_STEP);
    // Two places is the old precision. A finer step, such as 0.001, needs more.
    const digits = Math.max(2, stepDecimals(stepSize));
    const { control } = useFormContext();
    const units = allowedUnits.length > 0 ? allowedUnits : ([defaultUnit(allowedUnits)] as MeasurementUnit[]);

    return (
        <FormField
            control={control}
            name={name}
            render={({ field, fieldState }) => {
                const current: MeasurementValue = {
                    value: field.value?.value ?? null,
                    unit: isMeasurementUnit(field.value?.unit) ? field.value.unit : defaultUnit(units),
                };
                const displayValue =
                    current.value === null
                        ? null
                        : roundMeasurement(fromCanonicalMeasurement(current.value, current.unit), digits);
                const displayMin =
                    min == null ? 0 : roundMeasurement(fromCanonicalMeasurement(min, current.unit), digits);
                const displayMax =
                    max == null
                        ? Number.POSITIVE_INFINITY
                        : roundMeasurement(fromCanonicalMeasurement(max, current.unit), digits);
                const nestedValueError =
                    fieldState.error &&
                    typeof fieldState.error === "object" &&
                    "value" in fieldState.error &&
                    fieldState.error.value &&
                    typeof fieldState.error.value === "object" &&
                    "message" in fieldState.error.value
                        ? String((fieldState.error.value as { message?: unknown }).message ?? "")
                        : "";
                const errorMessage = fieldState.error?.message || nestedValueError || undefined;

                const commit = (next: MeasurementValue) => {
                    field.onChange(next);
                    onStoreChange?.(next);
                };

                return (
                    <FormItem>
                        <fieldset className="space-y-2" aria-invalid={errorMessage ? true : undefined}>
                            <legend className="font-medium text-lg">
                                {label}
                                {required ? " *" : ""}
                            </legend>
                            {description && <FormDescription>{description}</FormDescription>}
                            <div className="flex items-end gap-2">
                                <FormControl>
                                    <MeasurementStepper
                                        label={label}
                                        displayValue={displayValue}
                                        displayMin={displayMin}
                                        displayMax={displayMax}
                                        unit={current.unit}
                                        step={stepSize}
                                        digits={digits}
                                        onCommitDisplay={(nextDisplay) =>
                                            commit({
                                                value:
                                                    nextDisplay === null
                                                        ? null
                                                        : roundMeasurement(
                                                              toCanonicalMeasurement(nextDisplay, current.unit)
                                                          ),
                                                unit: current.unit,
                                            })
                                        }
                                    />
                                </FormControl>
                                <label className="flex flex-col gap-1 text-lg shrink-0">
                                    <span className="sr-only">{label} unit</span>
                                    <select
                                        aria-label={`${label} unit`}
                                        className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-500 rounded-md h-12 w-20 pl-3 pr-8 text-lg font-medium"
                                        value={current.unit}
                                        onChange={(event) =>
                                            commit({
                                                ...current,
                                                unit: event.target.value as MeasurementUnit,
                                            })
                                        }
                                    >
                                        {units.map((unit) => (
                                            <option key={unit} value={unit}>
                                                {unit}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                            {errorMessage ? (
                                <p className="font-medium text-red-500 dark:text-red-300 text-lg" role="alert">
                                    {errorMessage}
                                </p>
                            ) : (
                                <FormMessage />
                            )}
                        </fieldset>
                    </FormItem>
                );
            }}
        />
    );
}

interface StepperProps {
    label: string;
    displayValue: number | null;
    displayMin: number;
    displayMax: number;
    unit: MeasurementUnit;
    step: number;
    digits: number;
    onCommitDisplay: (value: number | null) => void;
}

function MeasurementStepper({
    label,
    displayValue,
    displayMin,
    displayMax,
    unit,
    step,
    digits,
    onCommitDisplay,
}: StepperProps) {
    const [focused, setFocused] = useState(false);
    const [draft, setDraft] = useState(formatDisplay(displayValue));

    useEffect(() => {
        if (!focused) setDraft(formatDisplay(displayValue));
    }, [displayValue, focused, unit]);

    const nudge = (direction: -1 | 1) => {
        const current = displayValue ?? displayMin;
        const next = nudgeByStep(current, direction, step, { min: displayMin, max: displayMax }, digits);
        onCommitDisplay(next);
        setDraft(formatDisplay(next));
    };

    return (
        <div
            className={cn(
                "flex items-stretch bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-500 rounded-md h-12 w-full",
                "focus-within:ring-2 focus-within:ring-indigo-300 ring-offset-white focus-within:ring-offset-2 dark:ring-offset-slate-900"
            )}
        >
            <button
                type="button"
                aria-label="Decrease"
                tabIndex={-1}
                className="flex justify-center items-center px-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={displayValue !== null && displayValue <= displayMin}
                onClick={() => nudge(-1)}
            >
                <Minus className="size-4" absoluteStrokeWidth strokeWidth={2.5} />
            </button>
            <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                role="spinbutton"
                aria-label={`${label} value`}
                aria-valuemin={displayMin}
                aria-valuemax={Number.isFinite(displayMax) ? displayMax : undefined}
                aria-valuenow={displayValue ?? undefined}
                className="flex-1 min-w-0 bg-transparent py-2 px-1 text-center text-lg font-medium text-slate-900 dark:text-slate-100 outline-none border-0 focus:outline-none focus:ring-0 selection:bg-indigo-200 dark:selection:bg-indigo-700"
                value={focused ? draft : formatDisplay(displayValue)}
                onFocus={() => {
                    setFocused(true);
                    setDraft(formatDisplay(displayValue));
                }}
                onChange={(event) => {
                    const raw = event.target.value;
                    if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
                    setDraft(raw);
                    if (raw === "") {
                        onCommitDisplay(null);
                        return;
                    }
                    if (/[.,]$/.test(raw) || raw === "-" || raw === "." || raw === "-.") return;
                    const parsed = parseDisplay(raw);
                    if (parsed === null || Number.isNaN(parsed)) return;
                    if (parsed < displayMin || parsed > displayMax) return;
                    onCommitDisplay(roundMeasurement(parsed, digits));
                }}
                onBlur={() => {
                    setFocused(false);
                    const cleaned = draft.trim().replace(/[.,]$/, "");
                    if (cleaned === "" || cleaned === "-") {
                        onCommitDisplay(null);
                        return;
                    }
                    const parsed = Number(cleaned);
                    if (!Number.isFinite(parsed)) {
                        setDraft(formatDisplay(displayValue));
                        return;
                    }
                    const clamped = Math.min(Math.max(parsed, displayMin), displayMax);
                    onCommitDisplay(roundMeasurement(clamped, digits));
                }}
            />
            <button
                type="button"
                aria-label="Increase"
                tabIndex={-1}
                className="flex justify-center items-center px-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={displayValue !== null && displayValue >= displayMax}
                onClick={() => nudge(1)}
            >
                <Plus className="size-4" absoluteStrokeWidth strokeWidth={2.5} />
            </button>
        </div>
    );
}
